import { useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Loader2, Minimize2, RefreshCw, PenLine, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const FAIL = "Couldn't draft the description — try again";

export function JDWriter({ job, setJob }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(false);
  const [drafted, setDrafted] = useState(false);
  const title = (job.title || "").trim();
  const run = async (mode) => {
    setBusy(mode); setErr(false);
    try {
      const { data: d } = await api.post("/jd/draft", { title, notes, mode, current: mode === "new" ? "" : job.jd_text || "" }, { timeout: 60000 });
      setJob((j) => ({ ...j, jd_text: d.jd_text, practice_area: d.practice_area || j.practice_area, pqe_min: d.pqe_min ?? j.pqe_min,
        pqe_max: d.pqe_max ?? j.pqe_max, bar_required: d.bar_required, location: j.location || d.location || "" }));
      setDrafted(true);
    } catch { setErr(true); }
    setBusy(null);
  };
  const tweak = (mode, Icon, label) => (
    <Button size="sm" variant="outline" onClick={() => run(mode)} disabled={!!busy || !title} data-testid={`jd-ai-${mode}-btn`}>
      {busy === mode ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />} {label}
    </Button>
  );
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 rounded-[6px] border border-line bg-brand-tint/50 p-4" data-tour="pt-jd-writer" data-testid="jd-writer">
      <div className="space-y-2">
        <Label htmlFor="jd-ai-notes">Notes for the AI writer (optional)</Label>
        <Textarea id="jd-ai-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Mumbai, 5–7 PQE, arbitration and commercial disputes, client-facing" data-testid="jd-ai-notes-input" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => run("new")} disabled={!title || !!busy} data-testid="jd-write-ai-btn">
          {busy === "new" ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />} {busy === "new" ? "Writing…" : "Write with AI"}
        </Button>
        {!title && <span className="text-xs text-ink-muted" data-testid="jd-ai-title-hint">Add a job title first</span>}
        {drafted && <>{tweak("new", RefreshCw, "Regenerate")}{tweak("shorter", Minimize2, "Make shorter")}{tweak("formal", Type, "Make more formal")}</>}
      </div>
      {drafted && !err && <p className="text-xs text-ink-muted" data-testid="jd-ai-prefill-note">Draft added below. Practice area, PQE and bar enrolment were filled in on the next step — check them before publishing.</p>}
      {err && <p role="alert" className="flex items-center gap-2 text-sm text-risk" data-testid="jd-ai-error"><AlertTriangle size={14} aria-hidden="true" /> {FAIL}</p>}
    </motion.div>
  );
}
